import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from "@mui/material";
import AppContext from "../../../context/appContext";
import SimpleButton from "../../../components/SimpleButton/SimpleButton";
import DownloadIcon from "../../../components/VectorIcons/DownloadIcon";
import useDownloadAttachment from "../../../hooks/useDownloadAttachment";
import { useAttachmentSelection } from "../../../hooks/useAttachmentSelection";
import { useAddendaAssociationsMap } from "../../../hooks/useAddendaAssociationsMap";
import { getAttachmentSelectionKey } from "../../../utils/getAttachmentSelectionKey";
import { getAddendumId, sortAddendaFiles } from "./releasedAddendum.utils";
import ReleasedAddendumTableHeader from "./ReleasedAddendumTableHeader";
import ReleasedAddendumItem from "./ReleasedAddendumItem";
import "../ReleasedListAttachments/styles/ReleasedListAttachments.scss";

export function ReleaseAddendumByContent({
  files = [],
  title,
  contentId,
  isChapter,
}) {
  const appContext = useContext(AppContext);
  const { downloadAndZipMultipleAttachments } = useDownloadAttachment();

  const { activeKeys, toggleActiveCheckBox, clearActive, resetSelection } =
    useAttachmentSelection();

  const { associationsByAddendumId, isLoadingAssociations } =
    useAddendaAssociationsMap({ files });

  const [orderBy, setOrderBy] = useState("receivedAt-desc");
  const [showOrderArrow, setShowOrderArrow] = useState(false);

  const sortedFiles = useMemo(
    () => sortAddendaFiles(files, orderBy, associationsByAddendumId),
    [files, orderBy, associationsByAddendumId]
  );

  // NOTE: applySelection() is NOT used here — it assumes a grouped
  // { parent, version } shape, but addenda are a flat array of plain
  // attachment objects, so `isActive` is derived directly from activeKeys.
  const stateFiles = useMemo(
    () =>
      sortedFiles.map((file) => ({
        ...file,
        isActive: activeKeys.has(getAttachmentSelectionKey(file)),
      })),
    [sortedFiles, activeKeys]
  );

  const selectableFiles = useMemo(
    () =>
      files.filter((file) => file?.fileId && file?.releaseManagement?.channel),
    [files]
  );

  const allSelected =
    !!selectableFiles.length &&
    selectableFiles.every((file) =>
      activeKeys.has(getAttachmentSelectionKey(file))
    );

  const selectedFiles = useMemo(
    () => stateFiles.filter((file) => file.isActive),
    [stateFiles]
  );

  const handleToggleAll = () => {
    resetSelection(
      allSelected
        ? []
        : selectableFiles.map(getAttachmentSelectionKey).filter(Boolean)
    );
  };

  const handleDownloadSelected = () => {
    downloadAndZipMultipleAttachments({
      contentId,
      filesArray: selectedFiles,
      origin: "textos-liberados",
      mode: 0,
      title,
      setLoading: appContext.setShowGlassLoading,
      callback: clearActive,
    });
  };

  useEffect(() => {
    clearActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <TableContainer component={Paper} elevation={0}>
        <Table
          sx={{
            "& tr:last-child td": {
              borderBottom: "none",
            },
          }}
        >
          <ReleasedAddendumTableHeader
            isChapter={isChapter}
            allSelected={allSelected}
            hasSelectableItems={!!selectableFiles.length}
            onToggleAll={handleToggleAll}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            showOrderArrow={showOrderArrow}
            setShowOrderArrow={setShowOrderArrow}
          />

          <TableBody>
            {stateFiles.map((file) => (
              <ReleasedAddendumItem
                key={getAttachmentSelectionKey(file) ?? file.fileId}
                file={file}
                title={title}
                contentId={contentId}
                isActive={file.isActive}
                onToggle={() => toggleActiveCheckBox(file)}
                associationsLabel={
                  associationsByAddendumId.get(getAddendumId(file))?.label
                }
                isLoadingAssociations={isLoadingAssociations}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!!selectableFiles.length && (
        <Box
          className="footer"
          sx={{
            backgroundColor: "#43444d",
            height: "56px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#fff", fontFamily: '"Globotipo Text"', ml: "10px" }}
          >
            {`Selecione o(s) anexo(s) que deseja baixar`}
          </Typography>

          <SimpleButton
            text="Baixar selecionados"
            classButton="buttonBlue"
            dataQA="button-download-addenda"
            onBreadcrumb={true}
            isValidForm={selectedFiles.length > 0}
            toValidate={true}
            clickEvent={handleDownloadSelected}
          >
            <DownloadIcon color="#fff" />
          </SimpleButton>
        </Box>
      )}
    </Box>
  );
}
